import { useEffect, useState } from "react";
import { MessageCircle } from "lucide-react";
import { t } from "@/i18n";

/* =============================================================================
 * ONBOARDING INTRO — src/pages/OnboardingIntro.jsx
 *
 * Pokémon-style dialogue. Used two ways:
 *  - From AuthEn.jsx, fresh signup: no initialName, asks for it (scene 0).
 *  - From Welcome.jsx, someone who signed up WITHOUT seeing this (Google
 *    OAuth, or the login->signup toggle): pass initialName={user.name} and
 *    it skips straight past the name-input scene.
 *
 * Click/tap ANYWHERE on screen: if the current line is still typing, it
 * completes instantly; if it's already fully shown AND the scene has no
 * interactive controls (name input / choice buttons), it advances.
 *
 * IMPORTANT: `level` and `minutes` are captured and passed to onComplete
 * but not wired to your onboarding/account logic yet.
 *
 * Props: initialName?: string, onComplete(name, { level, minutes })
 * ========================================================================== */

const FONT_LINK_ID = "harf-pixel-font";
function usePixelFont() {
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap";
    document.head.appendChild(link);
  }, []);
}

function useTypewriter(text, speed = 24) {
  const [count, setCount] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setCount(0);
    setDone(!text);
    if (!text) return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setCount(i);
      if (i >= text.length) {
        setDone(true);
        clearInterval(id);
      }
    }, speed);
    return () => clearInterval(id);
  }, [text, speed]);

  const skip = () => {
    setCount(text.length);
    setDone(true);
  };

  return { shown: text.slice(0, count), done, skip };
}

const LEVEL_OPTIONS = [
  {
    id: "fluent",
    arabic: true,
    label: "أعرف القراءة والكتابة والتحدث، وأريد فقط تحسين مفرداتي",
  },
  {
    id: "beginner",
    arabic: false,
    label:
      "Ba3ref n9ra w nfham shwaya, walakin ma 3andich qawa3id nahawiya, w bghit natahassan",
  },
  {
    id: "confused",
    arabic: false,
    label: "??? What did you say, I don't understand!",
  },
];
const MINUTE_OPTIONS = [10, 20, 30];

const GREETING =
  "Hello... hmm... sorry, I woke up from a long nap and I forgot your name... what is it again?";
const LEVEL_Q = "ما هو مستواك في اللغة العربية؟";
const MINUTES_Q =
  "Ahah sorry, I got a bit ahead of myself there, I meant to ask about your Arabic level, but I forgot you're just starting out with Harf! How many minutes a day are you ready to spend on your Arabic and growing your oasis?";
const CLOSING =
  "Noted! Have a great journey on Harf ;) I'll let you enjoy the platform now!";

export default function OnboardingIntro({ initialName = "", onComplete }) {
  usePixelFont();
  // Scene 0 = name (skipped when initialName is already known), 1 = welcome,
  // 2 = level question, 3 = minutes question, 4 = closing/final.
  const [scene, setScene] = useState(initialName ? 1 : 0);
  const [name, setName] = useState(initialName);
  const [nameInput, setNameInput] = useState("");
  const [level, setLevel] = useState(null);
  const [minutes, setMinutes] = useState(null);

  const texts = [
    GREETING,
    `Ok, nice to meet you ${name || "there"}! Welcome to Harf, the app to learn Arabic.`,
    LEVEL_Q,
    MINUTES_Q,
    CLOSING,
  ];
  const arabicScene = scene === 2;
  const { shown, done, skip } = useTypewriter(texts[scene]);

  const hasControls = scene === 0 || scene === 2 || scene === 3;
  const isFinal = scene === 4;

  // Scene 1 ("welcome") has no controls and isn't final: it's a pure
  // transition line, so it flows into the next scene on its own instead of
  // waiting for a click. Every other stop is an actual question/action.
  useEffect(() => {
    if (!done || hasControls || isFinal) return;
    const timer = setTimeout(() => setScene((s) => s + 1), 900);
    return () => clearTimeout(timer);
  }, [done, hasControls, isFinal]);

  const handleRootClick = (e) => {
    if (e.target.closest(".onb-interactive")) return;
    if (!done) skip();
  };

  return (
    <div onClick={handleRootClick} className="onb-root">
      <style>{`
        .onb-root { min-height: 100dvh; background: #F2F2F4; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 24px; cursor: pointer; font-family: 'Press Start 2P', monospace; }
        .onb-logo { width: 56px; height: 56px; border-radius: 16px; background: #3B82F6; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 26px; font-weight: bold; margin-bottom: 22px; box-shadow: 0 10px 25px rgba(59,130,246,.35); animation: onb-float 3s ease-in-out infinite; }
        @keyframes onb-float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .onb-box { width: 100%; max-width: 620px; background: #fff; border: 4px solid #14121F; border-radius: 18px; padding: 28px 24px; box-shadow: 0 12px 0 rgba(20,18,31,0.12); }
        .onb-text { font-family: 'Press Start 2P', monospace; font-size: 13px; line-height: 2; color: #14121F; margin: 0; white-space: pre-wrap; }
        .onb-arabic { font-family: 'Segoe UI', Tahoma, sans-serif !important; font-size: 20px; line-height: 1.9; text-align: right; }
        .onb-cursor { animation: onb-blink 1s step-end infinite; color: #3B82F6; }
        @keyframes onb-blink { 50% { opacity: 0; } }
        .onb-interactive { margin-top: 18px; cursor: default; }
        .onb-input { width: 100%; padding: 12px 14px; border-radius: 10px; border: 2px solid #E7E4F2; font-family: 'Press Start 2P', monospace; font-size: 12px; outline: none; box-sizing: border-box; }
        .onb-input:focus { border-color: #3B82F6; }
        .onb-btn { margin-top: 12px; width: 100%; padding: 12px 16px; border-radius: 10px; background: #3B82F6; color: #fff; font-family: 'Press Start 2P', monospace; font-size: 11px; font-weight: bold; border: none; cursor: pointer; }
        .onb-btn:disabled { opacity: .4; cursor: not-allowed; }
        .onb-choice { width: 100%; text-align: left; padding: 12px 14px; border-radius: 10px; border: 2px solid #E7E4F2; background: #fff; margin-top: 10px; cursor: pointer; font-size: 13px; line-height: 1.6; font-family: system-ui, sans-serif; transition: border-color .15s, background .15s; }
        .onb-choice[dir="rtl"] { text-align: right; font-family: 'Segoe UI', Tahoma, sans-serif; font-size: 18px; }
        .onb-choice:hover { border-color: #3B82F6; background: #DCEAFE; }
        .onb-minutes-row { display: flex; gap: 10px; margin-top: 18px; }
        .onb-minutes-row .onb-choice { flex: 1; text-align: center; margin-top: 0; }
        .onb-founder { max-width: 620px; margin-top: 22px; font-family: system-ui, sans-serif; font-size: 13px; line-height: 1.7; color: #7A768E; white-space: pre-line; cursor: default; }
      `}</style>

      <div className="onb-logo arabic">ع</div>

      <div className="onb-box" dir={arabicScene ? "rtl" : "ltr"}>
        <p className={`onb-text ${arabicScene ? "onb-arabic" : ""}`}>
          {shown}
          {!done && <span className="onb-cursor">▌</span>}
        </p>

        {done && scene === 0 && (
          <div className="onb-interactive" onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              className="onb-input"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder={t("onboardingintro.your_name")}
              onKeyDown={(e) => {
                if (e.key === "Enter" && nameInput.trim()) {
                  setName(nameInput.trim());
                  setScene(1);
                }
              }}
            />
            <button
              className="onb-btn"
              disabled={!nameInput.trim()}
              onClick={() => {
                setName(nameInput.trim());
                setScene(1);
              }}
            >
              {t("onboardingintro.continue")}
            </button>
          </div>
        )}

        {done && scene === 2 && (
          <div className="onb-interactive" onClick={(e) => e.stopPropagation()}>
            {LEVEL_OPTIONS.map((o) => (
              <button
                key={o.id}
                className="onb-choice"
                dir={o.arabic ? "rtl" : "ltr"}
                onClick={() => {
                  setLevel(o.id);
                  setScene(3);
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        )}

        {done && scene === 3 && (
          <div
            className="onb-interactive onb-minutes-row"
            onClick={(e) => e.stopPropagation()}
          >
            {MINUTE_OPTIONS.map((m) => (
              <button
                key={m}
                className="onb-choice"
                onClick={() => {
                  setMinutes(m);
                  setScene(4);
                }}
              >
                {m} min
              </button>
            ))}
          </div>
        )}

        {done && isFinal && (
          <div className="onb-interactive" onClick={(e) => e.stopPropagation()}>
            <button
              className="onb-btn"
              onClick={() => onComplete?.(name, { level, minutes })}
            >
              {t("onboardingintro.ok")}
            </button>
          </div>
        )}
      </div>

      {done && isFinal && (
        <p className="onb-founder" onClick={(e) => e.stopPropagation()}>
          {
            "I've always been passionate about the Arabic language, but it's too complex to learn on a generic language app. So I spent the last few months building Harf, brick by brick. \n\nI put my heart, my resources, and my time into it. You're on the very first public version, thank you! That means you get the launch discount pricing, which will always stay applied for you as a thank-you for the chance you're giving me. A lot of new exercises and new ways to learn are coming. I'm working hard on the app.\n\nIf you run into any issue, you can reach me directly at info@learnharf.app or through the "
          }
          <MessageCircle
            size={13}
            style={{ display: "inline", verticalAlign: "middle" }}
          />
          {
            " tab on the platform.\n\nThank you, I hope you enjoy it \n\nThéo, the creator of Harf."
          }
        </p>
      )}
    </div>
  );
}
